/**
 * Machine d'états d'une œuvre (D13).
 *
 * Le statut est un champ explicite, mais personne ne l'écrit directement :
 * il est posé par les événements du métier. Une facture émise vend l'œuvre,
 * une réservation la retient jusqu'à son terme, un dépôt la sort, un retour
 * la rend disponible. La correction à la main existe, elle exige une raison,
 * et elle est journalisée.
 */
import { AppError } from './errors.js';
export const TYPES_EVENEMENT_OEUVRE = [
    'facture_emise',
    'vente_simple',
    'reservation',
    'reservation_expiree',
    'depot',
    'pret',
    'retour',
    'location_debut',
    'location_fin',
    'retrait',
    'archivage',
    'correction_manuelle',
];
/**
 * La table des transitions : pour chaque statut, l'événement admis et le statut
 * qu'il pose. Ce qui n'y figure pas est interdit.
 *
 * Lectures retenues, que le document laisse ouvertes :
 * - une œuvre en dépôt ou en prêt peut se vendre : la facture l'emporte ;
 * - une œuvre réservée ne part ni en dépôt ni en prêt : on lève d'abord la
 *   réservation, ou on corrige à la main ;
 * - seule une œuvre disponible se loue ; louée, elle revient ou se vend par option ;
 * - une œuvre vendue ou archivée ne bouge plus qu'à l'archivage ou à la main.
 */
export const TRANSITIONS = {
    disponible: {
        facture_emise: 'vendue',
        vente_simple: 'vendue',
        reservation: 'reservee',
        location_debut: 'louee',
        depot: 'en_depot',
        pret: 'pretee',
        retrait: 'retiree',
        archivage: 'archivee',
    },
    reservee: {
        facture_emise: 'vendue',
        vente_simple: 'vendue',
        reservation_expiree: 'disponible',
        retrait: 'retiree',
        archivage: 'archivee',
    },
    vendue: {
        archivage: 'archivee',
    },
    en_depot: {
        facture_emise: 'vendue',
        vente_simple: 'vendue',
        retour: 'disponible',
        retrait: 'retiree',
        archivage: 'archivee',
    },
    pretee: {
        facture_emise: 'vendue',
        vente_simple: 'vendue',
        retour: 'disponible',
        retrait: 'retiree',
        archivage: 'archivee',
    },
    louee: {
        location_fin: 'disponible',
        facture_emise: 'vendue',
    },
    retiree: {
        archivage: 'archivee',
    },
    archivee: {},
};
/** Erreur posée quand un événement ne s'applique pas au statut courant. */
export class TransitionInterditeError extends AppError {
    statut;
    evenement;
    constructor(statut, evenement, message) {
        super('oeuvre.transition_interdite', message, { statut, evenement });
        this.name = 'TransitionInterditeError';
        this.statut = statut;
        this.evenement = evenement;
    }
}
/** Erreur posée quand une correction à la main arrive sans raison (D13). */
export class RaisonRequiseError extends AppError {
    constructor() {
        super('oeuvre.raison_obligatoire', 'Une correction de statut demande une raison. Écrivez-la, elle sera journalisée.');
        this.name = 'RaisonRequiseError';
    }
}
/** Les événements que le statut courant accepte, correction à la main exclue. */
export function transitionsPossibles(statut) {
    return Object.keys(TRANSITIONS[statut]);
}
/** Cet événement s'applique-t-il au statut courant ? */
export function transitionAutorisee(statut, evenement) {
    if (evenement === 'correction_manuelle')
        return true;
    return evenement in TRANSITIONS[statut];
}
/**
 * Applique un événement à un statut et renvoie le statut qui en découle.
 *
 * @throws {TransitionInterditeError} quand l'événement ne s'applique pas.
 * @throws {RaisonRequiseError} quand une correction à la main arrive sans raison.
 */
export function applyEvent(statut, event) {
    if (event.type === 'correction_manuelle') {
        if (event.raison.trim() === '') {
            throw new RaisonRequiseError();
        }
        if (event.vers === statut) {
            throw new TransitionInterditeError(statut, event.type, `L'œuvre est déjà « ${statut} ». Choisissez un autre statut.`);
        }
        return event.vers;
    }
    const suite = TRANSITIONS[statut];
    const cible = suite[event.type];
    if (cible === undefined) {
        throw new TransitionInterditeError(statut, event.type, `Une œuvre « ${statut} » n'accepte pas l'événement « ${event.type} ». ` +
            `Corrigez le statut à la main en indiquant une raison.`);
    }
    return cible;
}
//# sourceMappingURL=status.js.map